import { useState, useEffect } from 'react';
import { Text, Button } from '@qwickapps/react-framework';
import { config } from '../../config/runtime';

interface Document {
  id: string;
  title: string;
  type: 'frd' | 'architecture' | 'spike' | 'adr' | 'review' | 'whiteboard' | 'spec';
  status?: 'draft' | 'pending' | 'approved' | 'rejected';
  updatedAt: string;
}

interface DocumentSearchProps {
  documents?: Document[];
  loading?: boolean;
  onSearch?: (query: string) => void;
  onSelect?: (doc: Document) => void;
}

const typeLabels = {
  frd: 'FRD',
  architecture: 'Arch',
  spike: 'Spike',
  adr: 'ADR',
  review: 'Review',
  whiteboard: 'Whiteboard',
  spec: 'Spec',
};

const typeColors = {
  frd: '#2196f3',
  architecture: '#9c27b0',
  spike: '#ff9800',
  adr: '#4caf50',
  review: '#00bcd4',
  whiteboard: '#e91e63',
  spec: '#795548',
};

export default function DocumentSearch({
  documents: documentsProp,
  loading: loadingProp = false,
  onSearch,
  onSelect,
}: DocumentSearchProps) {
  const [query, setQuery] = useState('');
  const [internalDocs, setInternalDocs] = useState<Document[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    if (documentsProp !== undefined) return;

    let cancelled = false;

    const load = async () => {
      setInternalLoading(true);
      try {
        const res = await fetch(`${config.apiUrl}/api/projects/thoughts?limit=50`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;

        const thoughts: Record<string, unknown>[] = Array.isArray(data)
          ? data
          : (data.thoughts ?? []);

        setInternalDocs(thoughts.map(t => ({
          id: String(t.id ?? ''),
          title: String(t.content ?? '').slice(0, 80),
          type: 'spec' as const,
          status: 'approved' as const,
          updatedAt: String(t.createdAt ?? new Date().toISOString()),
        })));
        setFetchError(false);
      } catch {
        if (!cancelled) {
          setFetchError(true);
          setInternalDocs([]);
        }
      } finally {
        if (!cancelled) setInternalLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [documentsProp]);

  const handleSearch = () => {
    onSearch?.(query);
  };

  const allDocs = documentsProp ?? internalDocs;
  const filteredDocs = query
    ? allDocs.filter(d => d.title.toLowerCase().includes(query.toLowerCase()))
    : allDocs;

  const loading = loadingProp || internalLoading;
  const isSelfManaged = documentsProp === undefined;

  const emptyMessage = isSelfManaged
    ? 'Connect your project documents to see them here'
    : 'No documents found. Try a different search.';

  return (
    <div>
      <Text variant="h5" content="Documents" gutterBottom />
      <Text
        variant="body2"
        content="Search and manage project documents"
        customColor="var(--theme-text-secondary)"
        gutterBottom
      />

      <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--theme-text-secondary)' }}>
            &#x2315;
          </span>
          <input
            data-testid="search-input"
            placeholder="Search documents..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            style={{
              width: '100%',
              paddingLeft: 32,
              paddingRight: 12,
              paddingTop: 10,
              paddingBottom: 10,
              background: 'var(--theme-bg-surface)',
              border: '1px solid var(--theme-border-color)',
              borderRadius: 4,
              color: 'var(--theme-text-primary)',
              fontSize: 14,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <Button onClick={handleSearch}>Search</Button>
      </div>

      <div style={{ marginTop: 24 }}>
        {loading ? (
          <Text variant="body2" content="Loading..." customColor="var(--theme-text-secondary)" />
        ) : filteredDocs.length === 0 ? (
          <Text
            variant="body2"
            content={emptyMessage}
            customColor="var(--theme-text-disabled)"
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filteredDocs.map(doc => (
              <DocumentItem
                key={doc.id}
                document={doc}
                onClick={() => onSelect?.(doc)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DocumentItem({ document, onClick }: { document: Document; onClick?: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: 16,
        borderRadius: 4,
        background: 'var(--theme-bg-surface)',
        border: '1px solid var(--theme-border-color)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <div
        style={{
          padding: '2px 8px',
          borderRadius: 4,
          background: typeColors[document.type],
          color: 'white',
          fontSize: 10,
          fontWeight: 'bold',
          flexShrink: 0,
        }}
      >
        {typeLabels[document.type]}
      </div>
      <div style={{ flex: 1 }}>
        <Text variant="body1" content={document.title} />
        <Text
          variant="caption"
          content={`Updated ${new Date(document.updatedAt).toLocaleDateString()}`}
          customColor="var(--theme-text-secondary)"
        />
      </div>
      {document.status && (
        <StatusBadge status={document.status} />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors = {
    draft: '#9e9e9e',
    pending: '#ff9800',
    approved: '#4caf50',
    rejected: '#f44336',
  };

  return (
    <div
      style={{
        padding: '2px 8px',
        borderRadius: 4,
        background: colors[status as keyof typeof colors],
        color: 'white',
        fontSize: 10,
        textTransform: 'uppercase',
        flexShrink: 0,
      }}
    >
      {status}
    </div>
  );
}
